import { useState } from "react";
import {
	Box,
	Typography,
	Divider,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Stack,
	IconButton,
	List,
	ListItem,
	ListItemText,
	Avatar,
	Collapse,
	TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import BlockIcon from "@mui/icons-material/Block";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ReplyIcon from "@mui/icons-material/Reply";
import SendIcon from "@mui/icons-material/Send";
import CancelIcon from "@mui/icons-material/Cancel";
import { CategoryChip, StatusChip } from "../utils/CategoryStatusChips";
import RichEditor from "../../../components/rich-editor";
import { generateButtonSx } from "../../../utils/elementor-style-override";

const isEmptyHtml = (html) => !html || html.replace(/<[^>]*>/g, "").trim() === "";

const getInitials = (name) =>
	(name || "")
		.split(" ")
		.map((n) => n[0])
		.join("")
		.toUpperCase()
		.slice(0, 2);

const getAvatarColor = (role) => (role?.toLowerCase() === "admin" ? "#1D54A2" : "#6B7280");

// Default placeholder text per status, shown as a hint — admin can edit/replace it
const NOTE_PLACEHOLDERS = {
	"In Progress": "Marked as in progress by admin.",
	Blocked: "Blocked by admin, awaiting more info from user.",
	Completed: "Marked as completed by admin.",
};

const ACTION_META = {
	"In Progress": { color: "#E08A1E", hover: "#C9710A", label: "Mark In Progress" },
	Blocked: { color: "#C0392B", hover: "#A12A2A", label: "Block" },
	Completed: { color: "#2E9959", hover: "#1E7E4D", label: "Complete" },
};

const DIALOG_PAPER_SX = {
	"& .MuiDialog-paper": {
		maxWidth: "900px !important",
		width: "calc(100% - 64px) !important",
	},
};

const NOTE_DIALOG_PAPER_SX = {
	"& .MuiDialog-paper": {
		maxWidth: "600px !important",
		width: "calc(100% - 64px) !important",
	},
};

const TicketDetailsDialog = ({ open, ticket, onClose, onStatusChange, onSendReply, isAgent, replyLoading = false, updateLoading = false }) => {
	const [showReplyEditor, setShowReplyEditor] = useState(false);
	const [replyContent, setReplyContent] = useState("<p></p>");
	const [noteDialogOpen, setNoteDialogOpen] = useState(false);
	const [pendingStatus, setPendingStatus] = useState(null);
	const [noteText, setNoteText] = useState("");
	const [noteError, setNoteError] = useState(false);
	const [statusError, setStatusError] = useState(false);

	const handleActionClick = (status) => {
		setPendingStatus(status);
		setNoteText("");
		setNoteError(false);
		setNoteDialogOpen(true);
	};

	const handleConfirmAction = async () => {
		const trimmed = noteText.trim();
		if (!trimmed) {
			setNoteError(true);
			return;
		}

		setStatusError(false);
		const result = await onStatusChange?.(pendingStatus, trimmed);

		if (result?.success === false) {
			setStatusError(true);
			return;
		}

		setNoteDialogOpen(false);
		setPendingStatus(null);
		setNoteText("");
		setNoteError(false);
		setStatusError(false);
	};

	const handleCloseNoteDialog = () => {
		if (updateLoading) return;
		setNoteDialogOpen(false);
		setPendingStatus(null);
		setNoteText("");
		setNoteError(false);
	};

	if (!ticket) return null;

	const isQuestion = ticket.category === "Question";
	const messages = ticket.messages || [];
	const history = ticket.history || [];
	const submittedBy = ticket?.submittedBy?.name || "";
	const submittedByRole = ticket?.submittedBy?.role || "";
	const dateSubmitted = ticket?.dateSubmitted || "";

	// The "description" is really just the first message ever posted when the ticket was created
	const firstMessageHtml = messages[0]?.message || "";
	const descriptionHtml = firstMessageHtml?.startsWith("<") ? firstMessageHtml : `<p>${firstMessageHtml}</p>`;

	const ticketDisplayId = ticket.ticketId || ticket.ticket_number;

	const actionMeta = pendingStatus ? ACTION_META[pendingStatus] : null;

	const handleSend = async () => {
		if (replyLoading) return;
		if (isEmptyHtml(replyContent)) return;
		const result = await onSendReply?.(replyContent);
		if (result?.success) {
			setReplyContent("<p></p>");
			setShowReplyEditor(false);
		}
	};

	const handleCancelReply = () => {
		setReplyContent("<p></p>");
		setShowReplyEditor(false);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={DIALOG_PAPER_SX}>
			<DialogTitle
				sx={{
					bgcolor: "#1D54A2",
					color: "#fff",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Box>
					<Typography variant="subtitle2" sx={{ opacity: 0.8, letterSpacing: 1 }}>
						{ticketDisplayId}
					</Typography>
					<Typography variant="h6" sx={{ fontWeight: 700, color: "#EBF5FF" }}>
						{ticket.subject}
					</Typography>
				</Box>
				<IconButton onClick={onClose} sx={{ color: "#fff" }}>
					<CloseIcon sx={{ fontSize: 18 }} />
				</IconButton>
			</DialogTitle>

			<DialogContent sx={{ mt: 3 }}>
				<Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
					<CategoryChip value={ticket.category} />
					<StatusChip value={ticket.status} />
				</Stack>

				{submittedBy && (
					<>
						<Typography variant="caption" sx={{ color: "text.secondary" }}>
							Submitted by
						</Typography>
						<Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
							{submittedBy} ({submittedByRole}) — {dateSubmitted}
						</Typography>
					</>
				)}

				{/* Description — hidden for Question tickets since first message is in discussion */}
				{!isQuestion && (
					<>
						<Typography variant="caption" sx={{ color: "text.secondary" }}>
							Description
						</Typography>
						<Box sx={{ mb: 3 }}>
							<RichEditor value={descriptionHtml} readOnly />
						</Box>
					</>
				)}

				{/* Discussion thread — only for Question tickets */}
				{isQuestion && (
					<>
						<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
							Discussion
						</Typography>

						<Stack spacing={2} sx={{ mb: 2 }}>
							{messages.map((entry, idx) => {
								const isAdmin = entry.sender?.role?.toLowerCase() === "admin";
								return (
									<Stack key={idx} direction={isAdmin ? "row-reverse" : "row"} spacing={1.5} alignItems="flex-start">
										<Avatar
											sx={{
												width: 34,
												height: 34,
												fontSize: "0.75rem",
												bgcolor: getAvatarColor(entry.sender?.role),
												flexShrink: 0,
											}}
										>
											{getInitials(entry.sender?.name)}
										</Avatar>
										<Box sx={{ maxWidth: "75%" }}>
											<Stack direction={isAdmin ? "row-reverse" : "row"} spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
												<Typography variant="caption" sx={{ fontWeight: 700, color: isAdmin ? "#1D54A2" : "text.primary" }}>
													{entry.sender?.name}
												</Typography>
												<Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
													{entry.created_at}
												</Typography>
											</Stack>
											<Box
												sx={{
													bgcolor: isAdmin ? "#EBF5FF" : "#F3F4F6",
													borderRadius: isAdmin ? "12px 2px 12px 12px" : "2px 12px 12px 12px",
													px: 2,
													py: 1,
													border: "1px solid",
													borderColor: isAdmin ? "#BFDBFE" : "#E5E7EB",
												}}
											>
												<RichEditor value={entry.message} readOnly />
											</Box>
										</Box>
									</Stack>
								);
							})}
						</Stack>

						{/* Reply editor */}
						<Collapse in={showReplyEditor}>
							<Box
								sx={{
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 2,
									overflow: "hidden",
									mb: 1.5,
								}}
							>
								<Box sx={{ p: 1, bgcolor: "#F8FAFC", borderBottom: "1px solid", borderColor: "divider" }}>
									<Typography variant="caption" sx={{ fontWeight: 600, color: "#1D54A2" }}>
										{isAgent ? "Agent Reply" : "Admin Reply"}
									</Typography>
								</Box>
								<Box sx={{ p: 1 }}>
									<RichEditor value={replyContent} onChange={setReplyContent} placeholder="Type your response here..." />
								</Box>
							</Box>
							<Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 2 }}>
								<Button
									size="small"
									variant="outlined"
									startIcon={<CancelIcon />}
									onClick={handleCancelReply}
									disabled={replyLoading}
									color="inherit"
									sx={{
										bgcolor: "#F4F4F5 !important",
										color: "#1D54A2",
										borderColor: "#E5E7EB",
										textTransform: "none",
										...generateButtonSx(
											{ bg: "#E9EAEC !important", text: "#1D54A2" },
											{ bg: "#E9EAEC !important", text: "#1D54A2" }
										),
									}}
								>
									Cancel
								</Button>
								<Button
									size="small"
									variant="contained"
									startIcon={<SendIcon />}
									onClick={handleSend}
									disabled={replyLoading || isEmptyHtml(replyContent)}
									sx={{
										bgcolor: "#1D54A2 !important",
										color: "#fff",
										"&.Mui-disabled": {
											bgcolor: "#A9B7D0 !important",
											color: "#fff",
										},
										...generateButtonSx(
											{ bg: "#174A8B !important", text: "#FFFFFF" },
											{ bg: "#174A8B !important", text: "#FFFFFF" }
										),
									}}
								>
									Send Reply
								</Button>
							</Stack>
						</Collapse>
						{/* Question tickets: Respond button */}
						{isQuestion && !showReplyEditor && ticket.status !== "Completed" && (
							<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
								<Button
									variant="contained"
									fullWidth
									startIcon={<ReplyIcon />}
									onClick={() => setShowReplyEditor(true)}
									sx={{ bgcolor: "#1D54A2 !important", "&:hover": { bgcolor: "#174490" }, gap: 1 }}
								>
									Respond
								</Button>
							</Box>
						)}
					</>
				)}

				<Divider sx={{ mb: 2 }} />

				<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
					Activity Log
				</Typography>
				<List dense disablePadding>
					{history.map((entry, idx) => (
						<ListItem key={idx} disableGutters sx={{ alignItems: "flex-start" }}>
							<ListItemText
								primary={
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										{entry.status}
										<Typography component="span" variant="caption" sx={{ color: "text.secondary", ml: 1 }}>
											{entry.created_at}
										</Typography>
									</Typography>
								}
								secondary={entry.note}
							/>
						</ListItem>
					))}
				</List>
			</DialogContent>

			{!isAgent && (
				<DialogActions sx={{ px: 3, pb: 3, flexWrap: "wrap", gap: 1 }}>
					{/* Non-question tickets: original action buttons */}
					{!isQuestion && (
						<>
							{ticket.status !== "In Progress" && ticket.status !== "Completed" && (
								<Button
									variant="contained"
									startIcon={<PlayArrowIcon />}
									disabled={updateLoading}
									onClick={() => handleActionClick("In Progress")}
									sx={{ bgcolor: "#E08A1E !important", "&:hover": { bgcolor: "#C9710A" } }}
								>
									Mark In Progress
								</Button>
							)}
							{ticket.status !== "Blocked" && ticket.status !== "Completed" && (
								<Button
									variant="contained"
									startIcon={<BlockIcon />}
									disabled={updateLoading}
									onClick={() => handleActionClick("Blocked")}
									sx={{ bgcolor: "#C0392B !important", "&:hover": { bgcolor: "#A12A2A" } }}
								>
									Block
								</Button>
							)}
						</>
					)}

					{ticket.status !== "Completed" && (
						<Button
							variant="contained"
							startIcon={<DoneAllIcon />}
							onClick={() => handleActionClick("Completed")}
							disabled={updateLoading}
							sx={{ bgcolor: "#2E9959 !important", "&:hover": { bgcolor: "#1E7E4D" } }}
						>
							Complete
						</Button>
					)}
				</DialogActions>
			)}

			{/* Status-change dialog with required note, feeds the Activity Log */}
			<Dialog
				open={noteDialogOpen}
				onClose={updateLoading ? undefined : handleCloseNoteDialog}
				maxWidth="sm"
				fullWidth
				sx={NOTE_DIALOG_PAPER_SX}
			>
				<DialogTitle
					sx={{
						bgcolor: actionMeta?.color || "#1D54A2",
						color: "#fff",
						fontWeight: 700,
						fontSize: "2rem !important",
					}}
				>
					{actionMeta ? `Confirm: ${actionMeta.label}` : "Confirm Action"}
				</DialogTitle>
				<DialogContent sx={{ mt: 3 }}>
					<Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
						{pendingStatus
							? `Are you sure you want to mark this ticket as ${pendingStatus}? Add a note describing the next step — this will be recorded in the Activity Log.`
							: ""}
					</Typography>
					<TextField
						autoFocus
						fullWidth
						multiline
						minRows={3}
						maxRows={6}
						value={noteText}
						disabled={updateLoading}
						onChange={(e) => {
							setNoteText(e.target.value);
							if (noteError) setNoteError(false);
						}}
						placeholder={pendingStatus ? NOTE_PLACEHOLDERS[pendingStatus] : ""}
						error={noteError || statusError}
						helperText={
							noteError ? "A note is required before continuing." : statusError ? "Failed to update status. Please try again." : " "
						}
					/>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 3 }}>
					<Button
						variant="outlined"
						color="inherit"
						startIcon={<CancelIcon />}
						onClick={handleCloseNoteDialog}
						disabled={updateLoading}
						sx={{
							bgcolor: "#F4F4F5 !important",
							color: "#1D54A2",
							borderColor: "#E5E7EB",
							textTransform: "none",
							...generateButtonSx({ bg: "#E9EAEC !important", text: "#1D54A2" }, { bg: "#E9EAEC !important", text: "#1D54A2" }),
						}}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleConfirmAction}
						disabled={updateLoading}
						sx={{
							bgcolor: `${actionMeta?.color || "#1D54A2"} !important`,
							"&:hover": { bgcolor: actionMeta?.hover || "#174490" },
							"&.Mui-disabled": {
								bgcolor: `${actionMeta?.color || "#1D54A2"} !important`,
								opacity: 0.6,
								color: "#fff",
							},
						}}
					>
						{updateLoading ? "Updating..." : "Yes, Continue"}
					</Button>
				</DialogActions>
			</Dialog>
		</Dialog>
	);
};

export default TicketDetailsDialog;
